import React from "react";

import { Dialog, Small } from "@fronterahealth/frontera-ui-components";

export interface DomainChangeSummary {
  /** Stable id for React keys — pending client_id for new domains, Relay id otherwise. */
  key: string;
  /** Display name of the domain (incl. "Uncategorized"). */
  label: string;
  /** True if the domain itself is new and will be created. */
  isNew: boolean;
  /** True if the domain itself is being deleted. */
  isDeleted: boolean;
  /** "Renamed", "Description changed", etc. */
  metaChanges: string[];
  /** Goals that will be removed from this domain (display names). */
  removedGoals: string[];
  /** Goals that will land in this domain (display names). */
  addedGoals: string[];
}

interface ConfirmChangesDialogProps {
  open: boolean;
  isPending: boolean;
  summary: DomainChangeSummary[];
  onCancel: () => void;
  onConfirm: () => void;
}

export const ConfirmChangesDialog: React.FC<ConfirmChangesDialogProps> = ({
  open,
  isPending,
  summary,
  onCancel,
  onConfirm,
}) => {
  return (
    <Dialog
      open={open}
      setOpen={() => {}}
      title="Confirm changes"
      size="2xl"
      showCloseButton={false}
      primaryButton={{
        text: isPending ? "Saving…" : "Save changes",
        onClick: onConfirm,
        disabled: isPending,
      }}
      secondaryButton={{ text: "Back", onClick: onCancel, disabled: isPending }}
    >
      <div className="space-y-4 py-2">
        {summary.length === 0 ? (
          <Small colorType="secondary">No changes to apply.</Small>
        ) : (
          summary.map((entry) => (
            <div key={entry.key} className="rounded-md border border-border-medium p-3">
              <div className="flex items-center gap-2">
                <span className="font-medium text-text-primary">{entry.label}</span>
                {entry.isNew && <span className="rounded-sm bg-green-100 px-2 py-0.5 text-xs text-green-700">New</span>}
                {entry.isDeleted && (
                  <span className="rounded-sm bg-red-100 px-2 py-0.5 text-xs text-red-700">Deleted</span>
                )}
              </div>
              {entry.metaChanges.length > 0 && (
                <ul className="mt-1 list-disc pl-5 text-xs text-text-secondary">
                  {entry.metaChanges.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              )}
              {(entry.addedGoals.length > 0 || entry.removedGoals.length > 0) && (
                <ul className="mt-2 space-y-0.5 text-sm">
                  {entry.removedGoals.map((g) => (
                    <li key={`rm-${g}`} className="text-red-600">
                      <span className="mr-1 inline-block w-3">−</span>
                      {g}
                    </li>
                  ))}
                  {entry.addedGoals.map((g) => (
                    <li key={`add-${g}`} className="text-green-700">
                      <span className="mr-1 inline-block w-3">+</span>
                      {g}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))
        )}
      </div>
    </Dialog>
  );
};
